import { useTranslation } from "@equiem/web-ng-lib";
import {
  Button,
  ProgressCircle,
  Site,
  useShowError,
  Viewer,
} from "@equiem/web-ng-lib";
import React, { MutableRefObject, useContext } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  CommentFragmentFragment,
  useAddCommentMutation,
} from "../../generated/gateway-client";
import { ContentType } from "../../lib/ContentType";
import { mapContentType } from "../../lib/mapContentType";
import { CommentAvatar } from "./CommentAvatar";
import { updateCommentCount } from "./updateCommentCount";

interface Props {
  contentUuid: string;
  contentType: ContentType;
  cancelable?: boolean;
  parentUuid?: string;
  className?: string;
  bodyRef?: MutableRefObject<HTMLTextAreaElement | undefined>;
  onAddComment?: (
    args: Omit<Props, "onAddComment" | "className"> & {
      comment: CommentFragmentFragment;
    },
  ) => Promise<void>;
  onCancel?: () => Promise<void>;
}

interface Inputs {
  body: string;
}

export const CommentForm: React.FC<Props> = ({
  contentUuid,
  contentType,
  parentUuid,
  cancelable = false,
  className = "",
  bodyRef,
  onAddComment = () => undefined,
  onCancel = () => undefined,
}) => {
  const { t } = useTranslation();
  const viewer = useContext(Viewer);
  const site = useContext(Site);
  const showError = useShowError();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Inputs>({
    mode: "onSubmit",
  });
  const mappedContentType = mapContentType(contentType);
  const [addComment, { loading, client }] = useAddCommentMutation();

  const onSubmit = async ({ body }: Inputs) => {
    try {
      const result = await addComment({
        variables: {
          contentUuid,
          contentType: mappedContentType,
          parentUuid,
          body,
        },
      });

      if (result.data?.addComment?.comment != null) {
        reset();

        await onAddComment({
          comment: result.data.addComment.comment,
          contentUuid,
          contentType,
          parentUuid,
        });

        if (client != null) {
          updateCommentCount({ contentType, contentUuid, client });
        }
      } else {
        showError(t("common.somethingWrongTryAgainLater"));
      }
    } catch (e) {
      showError(e);
    }
  };

  const registerBody = register("body", { required: t("main.required") });

  return (
    <Form
      className={`comment-form ${className} d-flex pb-4`}
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="pr-2 pr-md-3">
        <CommentAvatar avatar={viewer?.profile?.avatar} />
      </div>
      <div className="w-100">
        <Form.Group>
          <Form.Control
            {...registerBody}
            as="textarea"
            name="body"
            className="pt-2"
            placeholder={t("main.yourComment")}
            rows={3}
            ref={(e: HTMLTextAreaElement) => {
              registerBody.ref(e);
              if (bodyRef != null) {
                bodyRef.current = e;
              }
            }}
            isInvalid={errors.body != null}
          />
          {errors.body != null ? (
            <Form.Control.Feedback type="invalid">
              {errors.body.message}
            </Form.Control.Feedback>
          ) : null}
        </Form.Group>
        <Button variant="solid" disabled={loading}>
          {loading ? (
            <ProgressCircle
              size="sm"
              color={site.primaryContrastColour}
              className="mr-1"
            />
          ) : null}
          {t("common.send")}
        </Button>
        {cancelable ? (
          <Button variant="link" type="button" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        ) : null}
      </div>
    </Form>
  );
};
